import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, ShoppingCart, Eye, Plus, FileText, CreditCard, Trash2, Edit } from 'lucide-react';
import InvoiceGenerator from '../components/InvoiceGenerator';
import CustomerDetails from '../components/CustomerDetails';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, doc, query, where } from 'firebase/firestore';

const Sales = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [showCreateSaleModal, setShowCreateSaleModal] = useState(false);
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [showSelectOrderModal, setShowSelectOrderModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [sales, setSales] = useState([]);
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSale, setSelectedSale] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [companyDetails, setCompanyDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [saleItems, setSaleItems] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState('not_paid');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [chequeNumber, setChequeNumber] = useState('');
  const [chequeDate, setChequeDate] = useState('');
  const [deliveryType, setDeliveryType] = useState('pickup');
  const [showPartialDeliveryModal, setShowPartialDeliveryModal] = useState(false);
  const [partialItems, setPartialItems] = useState([]);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedSaleForPayment, setSelectedSaleForPayment] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [customersSnapshot, salesSnapshot, ordersSnapshot, companySnapshot] = await Promise.all([
        getDocs(collection(db, 'customers')),
        getDocs(collection(db, 'sales')),
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'company'))
      ]);
      
      const customersData = customersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const salesData = salesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(sale => !sale.deleted);
      const ordersData = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(order => 
        !order.deleted
      );
      const companyData = companySnapshot.docs.length > 0 ? companySnapshot.docs[0].data() : {
        name: 'ADDICT',
        address: '123 Business Street',
        city: 'Colombo, Sri Lanka',
        phone: '+94 11 234 5678',
        email: 'info@addict.lk'
      };
      
      setCustomers(customersData);
      setSales(salesData);
      setOrders(ordersData);
      setCompanyDetails(companyData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="inventory-page">
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h3>Loading...</h3>
        </div>
      </div>
    );
  }

  const handleNewSale = () => {
    setShowSelectOrderModal(true);
  };

  const handleEditOrder = (order) => {
    setEditingOrder(order);
    setShowEditOrderModal(true);
  };

  const handleUpdateOrder = async (updatedOrder) => {
    try {
      await updateDoc(doc(db, 'orders', updatedOrder.id), updatedOrder);
      setShowEditOrderModal(false);
      setEditingOrder(null);
      loadData();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const handleSelectOrderForSale = (order) => {
    const readyItems = order.items.filter(item => 
      item.productionStatus === 'completed'
    ).map(item => ({
      ...item,
      selectedQuantity: (item.producedPairs || item.totalPairs || item.quantity || 0) - (item.deliveredQuantity || 0),
      availableQuantity: (item.producedPairs || item.totalPairs || item.quantity || 0) - (item.deliveredQuantity || 0),
      deliveredQuantity: item.deliveredQuantity || 0,
      price: item.pricePerPair || item.price || 0,
      quantity: item.totalPairs || item.quantity || 0
    }));
    
    setSaleItems(readyItems);
    setSelectedOrder(order);
    setPaymentStatus('not_paid');
    setPaymentAmount('');
    setPaymentNotes('');
    setChequeNumber('');
    setChequeDate('');
    setDeliveryType('pickup');
    setShowCreateSaleModal(true);
    setShowSelectOrderModal(false);
  };

  const updateInventory = async (selectedItems) => {
    for (const item of selectedItems) {
      const inventoryQuery = query(collection(db, 'inventory'), where('articleNumber', '==', item.articleNumber));
      const inventorySnapshot = await getDocs(inventoryQuery);
      
      if (!inventorySnapshot.empty) {
        const inventoryDoc = inventorySnapshot.docs[0];
        const inventoryData = inventoryDoc.data();
        const packSize = inventoryData.pairsPerPack || 8;
        const currentPacks = inventoryData.packsInStock || 0;
        const currentLoosePairs = inventoryData.loosePairs || 0;
        
        // Deduct from actual inventory when sale is made
        const totalAvailablePairs = (currentPacks * packSize) + currentLoosePairs;
        const pairsToReduce = item.selectedQuantity || item.quantity || 0;
        const remainingPairs = Math.max(0, totalAvailablePairs - pairsToReduce);
        const remainingFullPacks = Math.floor(remainingPairs / packSize);
        const remainingLoosePairs = remainingPairs % packSize;
        
        await updateDoc(doc(db, 'inventory', inventoryDoc.id), {
          packsInStock: remainingFullPacks,
          loosePairs: remainingLoosePairs,
          lastUpdated: new Date()
        });
      }
    }
  };

  const handleCreateSaleFromOrderSubmit = async () => {
    if (!selectedOrder || saleItems.filter(item => item.selectedQuantity > 0).length === 0) return;

    try {
      const selectedItems = saleItems.filter(item => item.selectedQuantity > 0);
      
      const deliveryDate = new Date();
      const saleData = {
        orderId: selectedOrder.id,
        customerId: selectedOrder.customerId,
        customerName: selectedOrder.customerName,
        customerPhone: selectedOrder.customerPhone || '',
        items: selectedItems.map(item => ({
          productId: item.productId || '',
          productName: item.productName || '',
          articleNumber: item.articleNumber || '',
          colour: item.colour || '',
          quantity: item.selectedQuantity || 0,
          price: item.price || item.pricePerPair || 0,
          collectedAt: deliveryDate,
          deliveredAt: deliveryDate,
          status: 'collected'
        })),
        totalAmount: selectedItems.reduce((sum, item) => sum + ((item.price || item.pricePerPair || 0) * (item.selectedQuantity || 0)), 0),
        paymentStatus: paymentStatus || 'not_paid',
        paymentNotes: paymentNotes || '',
        chequeNumber: paymentStatus === 'half_paid' && chequeNumber ? chequeNumber : '',
        chequeDate: paymentStatus === 'half_paid' && chequeDate ? chequeDate : '',
        createdAt: deliveryDate.toISOString(),
        deliveredAt: deliveryDate.toISOString(),
        isPartial: selectedItems.length < selectedOrder.items.filter(item => 
          item.productionStatus === 'completed' && (item.deliveredQuantity || 0) < (item.quantity || item.totalPairs || 0)
        ).length || selectedOrder.items.some(item => item.productionStatus !== 'completed')
      };

      const saleRef = await addDoc(collection(db, 'sales'), saleData);

      const updatedItems = selectedOrder.items.map(orderItem => {
        const saleItem = selectedItems.find(si => si.productId === orderItem.productId);
        if (saleItem) {
          const newDeliveredQuantity = (orderItem.deliveredQuantity || 0) + saleItem.selectedQuantity;
          const deliveryHistory = orderItem.deliveryHistory || [];
          
          deliveryHistory.push({
            quantity: saleItem.selectedQuantity,
            deliveredAt: deliveryDate,
            saleId: saleRef.id
          });
          
          return {
            ...orderItem,
            deliveredQuantity: newDeliveredQuantity,
            status: newDeliveredQuantity >= orderItem.quantity ? 'delivered' : 'partial',
            lastCollectedAt: deliveryDate,
            deliveryHistory
          };
        }
        return orderItem;
      });

      const allItemsDelivered = updatedItems.every(item => 
        item.status === 'delivered' || 
        (item.deliveredQuantity && item.deliveredQuantity >= item.quantity)
      );
      
      await updateDoc(doc(db, 'orders', selectedOrder.id), {
        items: updatedItems,
        status: allItemsDelivered ? 'completed' : 'ready'
      });

      await updateInventory(selectedItems);

      const existingSales = sales.filter(s => s.orderId === selectedOrder.id && !s.deleted);
      
      if (allItemsDelivered && existingSales.length > 0) {
        const allDeliveredItems = [];
        const deliveryHistory = [];
        
        existingSales.forEach(existingSale => {
          allDeliveredItems.push(...existingSale.items.map(item => ({
            ...item,
            deliveredAt: existingSale.deliveredAt
          })));
          deliveryHistory.push({
            deliveredAt: existingSale.deliveredAt,
            items: existingSale.items,
            saleId: existingSale.id
          });
        });
        
        allDeliveredItems.push(...selectedItems.map(item => ({
          ...item,
          deliveredAt: deliveryDate
        })));
        
        deliveryHistory.push({
          deliveredAt: deliveryDate,
          items: selectedItems,
          saleId: saleRef.id
        });
        
        const consolidatedSale = {
          orderId: selectedOrder.id,
          customerId: selectedOrder.customerId,
          customerName: selectedOrder.customerName,
          customerPhone: selectedOrder.customerPhone || '',
          items: allDeliveredItems,
          totalAmount: allDeliveredItems.reduce((sum, item) => sum + ((item.price || item.pricePerPair || 0) * (item.quantity || 0)), 0),
          paymentStatus: paymentStatus || 'not_paid',
          paymentNotes: paymentNotes || '',
          chequeNumber: paymentStatus === 'half_paid' && chequeNumber ? chequeNumber : '',
          chequeDate: paymentStatus === 'half_paid' && chequeDate ? chequeDate : '',
          createdAt: deliveryHistory[0]?.deliveredAt || deliveryDate.toISOString(),
          deliveredAt: deliveryDate.toISOString(),
          deliveryHistory,
          isFinalSale: true,
          orderCompleted: true
        };
        
        for (const existingSale of existingSales) {
          await updateDoc(doc(db, 'sales', existingSale.id), { deleted: true });
        }
        await updateDoc(doc(db, 'sales', saleRef.id), { deleted: true });
        
        const finalSaleRef = await addDoc(collection(db, 'sales'), consolidatedSale);
        
        setInvoiceData({
          ...consolidatedSale,
          id: finalSaleRef.id
        });
      } else {
        setInvoiceData({
          ...saleData,
          id: saleRef.id
        });
      }
      setShowInvoiceModal(true);

      setShowCreateSaleModal(false);
      setSaleItems([]);
      setPaymentStatus('not_paid');
      setPaymentNotes('');
      setChequeNumber('');
      setChequeDate('');
      setSelectedOrder(null);
      loadData();
    } catch (error) {
      console.error('Error creating sale:', error);
    }
  };

  const handlePartialDelivery = (sale) => {
    setSelectedSale(sale);
    const order = orders.find(o => o.id === sale.orderId);
    if (order) {
      const pendingItems = order.items.filter(item => 
        item.productionStatus === 'completed' && 
        (item.deliveredQuantity || 0) < (item.totalPairs || item.quantity || 0)
      ).map(item => ({
        ...item,
        selectedQuantity: 0,
        remainingQuantity: (item.totalPairs || item.quantity || 0) - (item.deliveredQuantity || 0)
      }));
      setPartialItems(pendingItems);
    }
    setShowPartialDeliveryModal(true);
  };

  const handleAddPartialDelivery = async () => {
    if (!selectedSale || partialItems.filter(item => item.selectedQuantity > 0).length === 0) return;

    try {
      const selectedItems = partialItems.filter(item => item.selectedQuantity > 0);
      
      const additionalDeliveryDate = new Date();
      const additionalSaleData = {
        orderId: selectedSale.orderId,
        customerId: selectedSale.customerId,
        customerName: selectedSale.customerName,
        customerPhone: selectedSale.customerPhone || '',
        items: selectedItems.map(item => ({
          productId: item.productId || '',
          productName: item.productName || '',
          articleNumber: item.articleNumber || '',
          colour: item.colour || '',
          quantity: item.selectedQuantity || 0,
          price: item.price || item.pricePerPair || 0,
          collectedAt: additionalDeliveryDate,
          deliveredAt: additionalDeliveryDate,
          status: 'collected'
        })),
        totalAmount: selectedItems.reduce((sum, item) => sum + ((item.price || item.pricePerPair || 0) * (item.selectedQuantity || 0)), 0),
        paymentStatus: 'not_paid',
        paymentNotes: 'Additional delivery',
        createdAt: additionalDeliveryDate.toISOString(),
        deliveredAt: additionalDeliveryDate.toISOString(),
        isPartial: true,
        parentSaleId: selectedSale.id
      };

      const additionalSaleRef = await addDoc(collection(db, 'sales'), additionalSaleData);

      const order = orders.find(o => o.id === selectedSale.orderId);
      const updatedItems = order.items.map(orderItem => {
        const selectedItem = selectedItems.find(si => si.productId === orderItem.productId);
        if (selectedItem) {
          const newDeliveredQuantity = (orderItem.deliveredQuantity || 0) + selectedItem.selectedQuantity;
          const deliveryHistory = orderItem.deliveryHistory || [];
          
          deliveryHistory.push({
            quantity: selectedItem.selectedQuantity,
            deliveredAt: additionalDeliveryDate,
            saleId: additionalSaleRef.id
          });
          
          return {
            ...orderItem,
            deliveredQuantity: newDeliveredQuantity,
            status: newDeliveredQuantity >= orderItem.quantity ? 'delivered' : 'partial',
            lastCollectedAt: additionalDeliveryDate,
            deliveryHistory
          };
        }
        return orderItem;
      });

      const allDelivered = updatedItems.every(item => item.status === 'delivered');
      
      await updateDoc(doc(db, 'orders', selectedSale.orderId), {
        items: updatedItems,
        status: allDelivered ? 'completed' : 'ready'
      });

      await updateInventory(selectedItems);

      const invoiceData = {
        ...additionalSaleData,
        id: additionalSaleRef.id,
        pendingItems: order.items.filter(item => 
          item.productionStatus === 'completed' && 
          (item.deliveredQuantity || 0) + (selectedItems.find(si => si.productId === item.productId)?.selectedQuantity || 0) < item.quantity
        )
      };
      
      setInvoiceData(invoiceData);
      setShowInvoiceModal(true);

      setShowPartialDeliveryModal(false);
      setPartialItems([]);
      setSelectedSale(null);
      loadData();
    } catch (error) {
      console.error('Error adding partial delivery:', error);
    }
  };

  const updatePaymentStatus = async (sale, newStatus, amount = '') => {
    try {
      const updateData = {
        paymentStatus: newStatus,
        paymentUpdatedAt: new Date()
      };
      
      if (newStatus === 'half_paid' && amount) {
        updateData.paidAmount = parseFloat(amount) || 0;
      }
      
      await updateDoc(doc(db, 'sales', sale.id), updateData);
      loadData();
    } catch (error) {
      console.error('Error updating payment status:', error);
    }
  };

  const deleteDoc = async (collectionName, docId) => {
    try {
      if (collectionName === 'orders') {
        const order = orders.find(o => o.id === docId);
        if (order) {
          // Delete all related sales
          const relatedSales = sales.filter(s => s.orderId === docId);
          for (const sale of relatedSales) {
            await updateDoc(doc(db, 'sales', sale.id), { deleted: true });
          }
          
          // Remove order from customer record
          if (order.customerId) {
            const customer = customers.find(c => c.id === order.customerId);
            if (customer && customer.orders) {
              const updatedOrders = customer.orders.filter(orderId => orderId !== docId);
              await updateDoc(doc(db, 'customers', order.customerId), {
                orders: updatedOrders
              });
            }
          }
        }
      }
      
      if (collectionName === 'sales') {
        const sale = sales.find(s => s.id === docId);
        if (sale) {
          // Check if this is the last sale for an order
          const otherSales = sales.filter(s => s.orderId === sale.orderId && s.id !== docId && !s.deleted);
          if (otherSales.length === 0) {
            // Reset order delivery status if no other sales exist
            const order = orders.find(o => o.id === sale.orderId);
            if (order) {
              const resetItems = order.items.map(item => ({
                ...item,
                deliveredQuantity: 0,
                status: item.productionStatus === 'completed' ? 'ready' : 'pending',
                deliveryHistory: []
              }));
              await updateDoc(doc(db, 'orders', sale.orderId), {
                items: resetItems,
                status: 'ready'
              });
            }
          }
          
          // Remove sale from customer record
          if (sale.customerId) {
            const customer = customers.find(c => c.id === sale.customerId);
            if (customer && customer.sales) {
              const updatedSales = customer.sales.filter(saleId => saleId !== docId);
              await updateDoc(doc(db, 'customers', sale.customerId), {
                sales: updatedSales
              });
            }
          }
        }
      }
      
      // Delete the main document
      await updateDoc(doc(db, collectionName, docId), { deleted: true });
      loadData();
    } catch (error) {
      console.error(`Error deleting ${collectionName}:`, error);
    }
  };

  const filteredOrders = orders.filter(o => {
    const orderNumber = String(o.id).slice(-6).padStart(6, '0');
    return !o.deleted && 
           (o.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            orderNumber.includes(searchTerm)) && 
           o.status !== 'completed';
  });

  const completedOrders = orders.filter(o => {
    const orderNumber = String(o.id).slice(-6).padStart(6, '0');
    return !o.deleted && 
           (o.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            orderNumber.includes(searchTerm)) && 
           o.status === 'completed';
  });

  const filteredSales = sales.filter(s => {
    const saleNumber = String(s.id).slice(-6).padStart(6, '0');
    return !s.deleted && 
           (s.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            saleNumber.includes(searchTerm));
  }).sort((a, b) => {
    // Sort by payment status: not_paid first, then half_paid, then full_paid
    const statusOrder = { 'not_paid': 0, 'half_paid': 1, 'full_paid': 2 };
    const aStatus = statusOrder[a.paymentStatus] || 0;
    const bStatus = statusOrder[b.paymentStatus] || 0;
    
    if (aStatus !== bStatus) {
      return aStatus - bStatus;
    }
    
    // If same payment status, sort by date (newest first)
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  const getPaginatedData = (data) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  const getTotalPages = (data) => Math.ceil(data.length / itemsPerPage);

  const getStatusColor = (status) => {
    switch(status) {
      case 'ready': return '#27ae60';
      case 'completed': return '#95a5a6';
      default: return '#666';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch(status) {
      case 'not_paid': return '#e74c3c';
      case 'half_paid': return '#f39c12';
      case 'full_paid': return '#27ae60';
      default: return '#666';
    }
  };

  return (
    <div className="inventory-page">
      <div className="inventory-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={16} style={{ marginRight: '8px' }} />Back
          </button>
          <h1 className="inventory-title">Sales Management</h1>
        </div>
        
        <div className="inventory-actions">
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#666' }} />
            <input
              type="text"
              placeholder="Search by customer name or number..."
              className="search-bar"
              style={{ paddingLeft: '45px' }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="btn btn-primary" onClick={handleNewSale}>
            <Plus size={16} style={{ marginRight: '8px' }} />New Sale
          </button>
        </div>
      </div>

      <div style={{ padding: '20px 30px', background: '#f8f9fa' }}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button 
            onClick={() => { setActiveTab('orders'); setCurrentPage(1); }}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              background: activeTab === 'orders' ? '#667eea' : '#e9ecef',
              color: activeTab === 'orders' ? 'white' : '#666',
              cursor: 'pointer'
            }}
          >
            Ready Orders ({filteredOrders.length})
          </button>
          <button 
            onClick={() => { setActiveTab('sales'); setCurrentPage(1); }}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              background: activeTab === 'sales' ? '#667eea' : '#e9ecef',
              color: activeTab === 'sales' ? 'white' : '#666',
              cursor: 'pointer'
            }}
          >
            Sales ({filteredSales.length})
          </button>
          <button 
            onClick={() => { setActiveTab('history'); setCurrentPage(1); }}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              background: activeTab === 'history' ? '#667eea' : '#e9ecef',
              color: activeTab === 'history' ? 'white' : '#666',
              cursor: 'pointer'
            }}
          >
            Order History ({completedOrders.length})
          </button>
          <button 
            onClick={() => { setActiveTab('customers'); setCurrentPage(1); }}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              background: activeTab === 'customers' ? '#667eea' : '#e9ecef',
              color: activeTab === 'customers' ? 'white' : '#666',
              cursor: 'pointer'
            }}
          >
            Customers ({customers.length})
          </button>
        </div>

        {activeTab === 'orders' && (
          <div>
            <div className="inventory-grid">
              {getPaginatedData(filteredOrders).map(order => {
              const readyItems = order.items.filter(item => 
                item.productionStatus === 'completed' && 
                (item.deliveredQuantity || 0) < (item.totalPairs || item.quantity || 0)
              );
              const deliveredItems = order.items.filter(item => 
                (item.deliveredQuantity || 0) >= (item.totalPairs || item.quantity || 0)
              );
              const partialItems = order.items.filter(item => 
                item.productionStatus === 'completed' && 
                (item.deliveredQuantity || 0) > 0 && 
                (item.deliveredQuantity || 0) < (item.totalPairs || item.quantity || 0)
              );
              const pendingItems = order.items.filter(item => 
                item.productionStatus !== 'completed'
              );
              const hasAnyReadyItems = readyItems.length > 0;
              const allItemsDelivered = deliveredItems.length === order.items.length;
              
              const orderTotal = order.total || 0;
              const paidAmount = order.paidAmount || 0;
              const remainingAmount = orderTotal - paidAmount;
              
              let paymentDisplay = '';
              if (order.paymentStatus === 'full_paid') {
                paymentDisplay = 'PAID';
              } else if (order.paymentStatus === 'half_paid') {
                paymentDisplay = `PAID: LKR ${paidAmount.toLocaleString()} | LEFT: LKR ${remainingAmount.toLocaleString()}`;
                if (order.chequeNumber) {
                  paymentDisplay += ` | Cheque: ${order.chequeNumber} (${order.chequePaid ? 'PAID' : 'PENDING'})`;
                }
              } else {
                paymentDisplay = 'NOT PAID';
                if (order.paymentMethod === 'cheque' && order.chequeNumber) {
                  paymentDisplay += ` | Cheque: ${order.chequeNumber} (${order.chequePaid ? 'PAID' : 'PENDING'})`;
                }
              }
              
              return (
                <div key={order.id} className="inventory-item" style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  border: '1px solid #e9ecef'
                }}>
                  <div className="item-header">
                    <div className="item-article">#{String(order.id).slice(-6).padStart(6, '0')}</div>
                    <div className="item-stock" style={{ 
                      color: allItemsDelivered ? '#95a5a6' : getStatusColor(order.status),
                      fontSize: '0.8rem'
                    }}>
                      {allItemsDelivered ? 'ALL DELIVERED' : order.status.toUpperCase()}
                    </div>
                  </div>
                  <div className="item-details">
                    <div className="item-detail">
                      <span className="item-detail-label">Customer:</span>
                      <span className="item-detail-value">{order.customerName}</span>
                    </div>
                    <div className="item-detail">
                      <span className="item-detail-label">Ready:</span>
                      <span className="item-detail-value">{readyItems.length} items</span>
                    </div>
                    <div className="item-detail">
                      <span className="item-detail-label">Pending:</span>
                      <span className="item-detail-value">{pendingItems.length} items</span>
                    </div>
                    <div className="item-detail">
                      <span className="item-detail-label">Partial:</span>
                      <span className="item-detail-value">{partialItems.length} items</span>
                    </div>
                    <div className="item-detail">
                      <span className="item-detail-label">Delivered:</span>
                      <span className="item-detail-value">{deliveredItems.length} items</span>
                    </div>
                    <div className="item-detail">
                      <span className="item-detail-label">Total:</span>
                      <span className="item-detail-value">LKR {order.total?.toLocaleString()}</span>
                    </div>
                    <div className="item-detail">
                      <span className="item-detail-label">Payment:</span>
                      <span className="item-detail-value" style={{ fontSize: '0.8rem' }}>{paymentDisplay}</span>
                    </div>
                  </div>
                  
                  {order.items && order.items.length > 0 && (
                    <div style={{ marginTop: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '8px' }}>
                      <h5 style={{ margin: '0 0 10px 0', fontSize: '0.9rem' }}>Items Status:</h5>
                      {order.items.map((item, index) => (
                        <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                          <div>
                            <span style={{ fontSize: '0.8rem' }}>{item.articleNumber} - {item.colour}</span>
                            {item.deliveredQuantity > 0 && (
                              <span style={{ fontSize: '0.7rem', color: '#666', marginLeft: '5px' }}>({item.deliveredQuantity}/{item.totalPairs || item.quantity})</span>
                            )}
                          </div>
                          {(item.deliveredQuantity || 0) >= (item.totalPairs || item.quantity || 0) ? (
                            <span style={{ color: '#95a5a6', fontSize: '0.8rem' }}>✓ DELIVERED</span>
                          ) : item.status === 'partial' || (item.deliveredQuantity || 0) > 0 ? (
                            <span style={{ color: '#f39c12', fontSize: '0.8rem' }}>◐ PARTIAL</span>
                          ) : item.productionStatus === 'completed' ? (
                            <span style={{ color: '#27ae60', fontSize: '0.8rem' }}>✓ READY</span>
                          ) : (
                            <span style={{ color: '#f39c12', fontSize: '0.8rem' }}>○ PENDING</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                    {hasAnyReadyItems && (
                      <button 
                        onClick={() => handleSelectOrderForSale(order)} 
                        style={{ 
                          background: '#27ae60', 
                          color: 'white', 
                          border: 'none', 
                          padding: '8px 12px', 
                          borderRadius: '8px', 
                          cursor: 'pointer', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '5px' 
                        }}
                      >
                        <ShoppingCart size={14} /> {allItemsDelivered ? 'All Delivered' : `Create Sale (${readyItems.length})`}
                      </button>
                    )}
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleEditOrder(order); }} 
                      style={{ 
                        background: '#3498db', 
                        color: 'white', 
                        border: 'none', 
                        padding: '8px 12px', 
                        borderRadius: '8px', 
                        cursor: 'pointer', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '5px' 
                      }}
                    >
                      <Edit size={14} /> Edit
                    </button>
                    <button 
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        if(window.confirm('Delete this order? This will also delete all related sales.')) {
                          deleteDoc('orders', order.id);
                        }
                      }} 
                      style={{ 
                        background: '#e74c3c', 
                        color: 'white', 
                        border: 'none', 
                        padding: '8px 12px', 
                        borderRadius: '8px', 
                        cursor: 'pointer', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '5px' 
                      }}
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              );
              })}
            </div>
            {getTotalPages(filteredOrders) > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                <button 
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="btn btn-secondary"
                >
                  Previous
                </button>
                <span style={{ padding: '10px 20px', background: 'white', borderRadius: '8px' }}>
                  Page {currentPage} of {getTotalPages(filteredOrders)}
                </span>
                <button 
                  onClick={() => setCurrentPage(Math.min(getTotalPages(filteredOrders), currentPage + 1))}
                  disabled={currentPage === getTotalPages(filteredOrders)}
                  className="btn btn-secondary"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'history' && (
          <div>
            <div className="inventory-grid">
              {getPaginatedData(completedOrders).map(order => (
                <div key={order.id} className="inventory-item" style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  border: '1px solid #e9ecef',
                  opacity: 0.8
                }}>
                  <div className="item-header">
                    <div className="item-article">#{String(order.id).slice(-6).padStart(6, '0')}</div>
                    <div className="item-stock" style={{ color: '#95a5a6', fontSize: '0.8rem' }}>
                      COMPLETED
                    </div>
                  </div>
                  <div className="item-details">
                    <div className="item-detail">
                      <span className="item-detail-label">Customer:</span>
                      <span className="item-detail-value">{order.customerName}</span>
                    </div>
                    <div className="item-detail">
                      <span className="item-detail-label">Total:</span>
                      <span className="item-detail-value">LKR {(order.total || 0).toLocaleString()}</span>
                    </div>
                    <div className="item-detail">
                      <span className="item-detail-label">Completed:</span>
                      <span className="item-detail-value">{new Date(order.updatedAt || order.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {getTotalPages(completedOrders) > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                <button 
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="btn btn-secondary"
                >
                  Previous
                </button>
                <span style={{ padding: '10px 20px', background: 'white', borderRadius: '8px' }}>
                  Page {currentPage} of {getTotalPages(completedOrders)}
                </span>
                <button 
                  onClick={() => setCurrentPage(Math.min(getTotalPages(completedOrders), currentPage + 1))}
                  disabled={currentPage === getTotalPages(completedOrders)}
                  className="btn btn-secondary"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
        {activeTab === 'sales' && (
          <div>
            <div className="inventory-grid" style={{
              gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))'
            }}>
              {getPaginatedData(filteredSales).map(sale => (
                <div key={sale.id} className="inventory-item" style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: window.innerWidth < 768 ? '16px' : '20px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  border: '1px solid #e9ecef'
                }}>
                  <div className="item-header">
                    <div className="item-article">#{String(sale.id).slice(-6).padStart(6, '0')}</div>
                    <div className="item-stock" style={{ color: getPaymentStatusColor(sale.paymentStatus) }}>
                      {sale.paymentStatus?.replace('_', ' ').toUpperCase()}
                    </div>
                  </div>
                  <div className="item-details">
                    <div className="item-detail">
                      <span className="item-detail-label">Customer:</span>
                      <span className="item-detail-value">{sale.customerName}</span>
                    </div>
                    <div className="item-detail">
                      <span className="item-detail-label">Items:</span>
                      <span className="item-detail-value">{sale.items?.length} products</span>
                    </div>
                    <div className="item-detail">
                      <span className="item-detail-label">Payment:</span>
                      <span className="item-detail-value">{sale.paymentMethod?.toUpperCase()}</span>
                    </div>
                    <div className="item-detail">
                      <span className="item-detail-label">Total:</span>
                      <span className="item-detail-value">LKR {sale.total?.toLocaleString()}</span>
                    </div>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    gap: window.innerWidth < 768 ? '8px' : '10px', 
                    marginTop: '15px', 
                    paddingTop: '15px', 
                    borderTop: '1px solid #eee',
                    flexWrap: 'wrap'
                  }}>
                    <button onClick={() => setSelectedSale(sale)} style={{ 
                      background: '#667eea', 
                      color: 'white', 
                      border: 'none', 
                      padding: window.innerWidth < 768 ? '6px 10px' : '8px 12px', 
                      borderRadius: '8px', 
                      cursor: 'pointer', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '5px',
                      fontSize: window.innerWidth < 768 ? '0.8rem' : '1rem'
                    }}>
                      <FileText size={14} /> Invoice
                    </button>
                    {sale.paymentStatus !== 'full_paid' && (
                      <button onClick={() => { setSelectedSaleForPayment(sale); setShowPaymentModal(true); }} style={{ 
                        background: '#f39c12', 
                        color: 'white', 
                        border: 'none', 
                        padding: window.innerWidth < 768 ? '6px 10px' : '8px 12px', 
                        borderRadius: '8px', 
                        cursor: 'pointer', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '5px',
                        fontSize: window.innerWidth < 768 ? '0.8rem' : '1rem'
                      }}>
                        <CreditCard size={14} /> Payment
                      </button>
                    )}
                    <button onClick={() => { if(window.confirm('Delete this sale? This will reset order delivery status if no other sales exist.')) deleteDoc('sales', sale.id); }} style={{ 
                      background: '#e74c3c', 
                      color: 'white', 
                      border: 'none', 
                      padding: window.innerWidth < 768 ? '6px 10px' : '8px 12px', 
                      borderRadius: '8px', 
                      cursor: 'pointer', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '5px',
                      fontSize: window.innerWidth < 768 ? '0.8rem' : '1rem'
                    }}>
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {getTotalPages(filteredSales) > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                <button 
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="btn btn-secondary"
                >
                  Previous
                </button>
                <span style={{ padding: '10px 20px', background: 'white', borderRadius: '8px' }}>
                  Page {currentPage} of {getTotalPages(filteredSales)}
                </span>
                <button 
                  onClick={() => setCurrentPage(Math.min(getTotalPages(filteredSales), currentPage + 1))}
                  disabled={currentPage === getTotalPages(filteredSales)}
                  className="btn btn-secondary"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'customers' && (
          <div>
            <div className="inventory-grid">
              {getPaginatedData(customers.filter(c => 
                c.shopName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                c.city?.toLowerCase().includes(searchTerm.toLowerCase())
              )).map(customer => {
                const customerOrders = orders.filter(o => o.customerId === customer.id && !o.deleted);
                const customerSales = sales.filter(s => s.customerId === customer.id && !s.deleted);
                const totalSpent = customerSales.reduce((sum, sale) => sum + (sale.totalAmount || sale.total || 0), 0);
                
                return (
                  <div key={customer.id} className="inventory-item" style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    border: '1px solid #e9ecef'
                  }}>
                    <div className="item-header">
                      <div className="item-article">{customer.shopName}</div>
                      <div className="item-stock" style={{ color: '#667eea', fontSize: '0.8rem' }}>
                        {customer.city}
                      </div>
                    </div>
                    <div className="item-details">
                      <div className="item-detail">
                        <span className="item-detail-label">Phone:</span>
                        <span className="item-detail-value">{customer.phoneNumber || 'N/A'}</span>
                      </div>
                      <div className="item-detail">
                        <span className="item-detail-label">Orders:</span>
                        <span className="item-detail-value">{customerOrders.length}</span>
                      </div>
                      <div className="item-detail">
                        <span className="item-detail-label">Sales:</span>
                        <span className="item-detail-value">{customerSales.length}</span>
                      </div>
                      <div className="item-detail">
                        <span className="item-detail-label">Total Spent:</span>
                        <span className="item-detail-value">LKR {totalSpent.toLocaleString()}</span>
                      </div>
                    </div>
                    
                    {customerOrders.length > 0 && (
                      <div style={{ marginTop: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '8px' }}>
                        <h5 style={{ margin: '0 0 10px 0', fontSize: '0.9rem' }}>Recent Orders:</h5>
                        {customerOrders.slice(0, 3).map((order, index) => (
                          <div key={index} style={{ fontSize: '0.8rem', marginBottom: '5px', color: '#666' }}>
                            #{order.id.slice(-6)} - {order.status.toUpperCase()} - LKR {(order.total || 0).toLocaleString()}
                          </div>
                        ))}
                        {customerOrders.length > 3 && (
                          <div style={{ fontSize: '0.7rem', color: '#999' }}>...and {customerOrders.length - 3} more</div>
                        )}
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                      <button onClick={() => setSelectedCustomer(customer)} style={{ background: '#3498db', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Eye size={14} /> View Details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {getTotalPages(customers.filter(c => 
              c.shopName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              c.city?.toLowerCase().includes(searchTerm.toLowerCase())
            )) > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                <button 
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="btn btn-secondary"
                >
                  Previous
                </button>
                <span style={{ padding: '10px 20px', background: 'white', borderRadius: '8px' }}>
                  Page {currentPage} of {getTotalPages(customers.filter(c => 
                    c.shopName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    c.city?.toLowerCase().includes(searchTerm.toLowerCase())
                  ))}
                </span>
                <button 
                  onClick={() => setCurrentPage(Math.min(getTotalPages(customers.filter(c => 
                    c.shopName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    c.city?.toLowerCase().includes(searchTerm.toLowerCase())
                  )), currentPage + 1))}
                  disabled={currentPage === getTotalPages(customers.filter(c => 
                    c.shopName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    c.city?.toLowerCase().includes(searchTerm.toLowerCase())
                  ))}
                  className="btn btn-secondary"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {showEditOrderModal && editingOrder && (
        <div className="modal-overlay" onClick={() => setShowEditOrderModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Edit Order #{editingOrder.id.slice(-6)}</h2>
            <div className="modal-form">
              <div className="form-group">
                <label>Customer: {editingOrder.customerName}</label>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={editingOrder.status} onChange={(e) => setEditingOrder({...editingOrder, status: e.target.value})}>
                  <option value="pending">Pending</option>
                  <option value="in-production">In Production</option>
                  <option value="ready">Ready</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditOrderModal(false)}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={() => handleUpdateOrder(editingOrder)}>Update Order</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSelectOrderModal && (
        <div className="modal-overlay" onClick={() => setShowSelectOrderModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Select Order for Sale</h2>
            <div className="modal-form">
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {orders.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>No orders found</div>
                ) : (
                  orders.filter(order => 
                    order.items && order.items.some(item => item.productionStatus === 'completed')
                  ).length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>No orders with ready items</div>
                  ) : (
                    orders.filter(order => 
                      order.items && order.items.some(item => item.productionStatus === 'completed') && !order.deleted && order.status !== 'completed'
                    ).map(order => {
                      const readyItems = order.items ? order.items.filter(item => item.productionStatus === 'completed') : [];
                      return (
                        <div key={order.id} style={{
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          padding: '15px',
                          marginBottom: '10px',
                          cursor: 'pointer',
                          backgroundColor: '#fff'
                        }} onClick={() => handleSelectOrderForSale(order)}>
                          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>#{order.id.slice(-6)} - {order.customerName}</div>
                          <div style={{ fontSize: '0.9rem', color: '#666' }}>
                            Ready items: {readyItems.length} of {order.items ? order.items.length : 0}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#27ae60', marginTop: '5px' }}>
                            {readyItems.map(item => `${item.articleNumber || 'N/A'}-${item.colour || 'N/A'}`).join(', ')}
                          </div>
                        </div>
                      );
                    })
                  )
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowSelectOrderModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreateSaleModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowCreateSaleModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Create Sale from Order #{selectedOrder.id.slice(-6)}</h2>
            <div className="modal-form">
              <div className="form-group">
                <label>Customer: {selectedOrder.customerName}</label>
              </div>
              
              <div className="form-group">
                <label>Select Items to Deliver</label>
                <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                  {saleItems.map((item, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '8px 0', 
                      borderBottom: index < saleItems.length - 1 ? '1px solid #ddd' : 'none'
                    }}>
                      <div>
                        <div style={{ fontWeight: '600', color: '#333' }}>({item.articleNumber}) {item.colour}</div>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>
                          Available: {item.availableQuantity} pairs × LKR {item.price || item.pricePerPair || 0}
                          <br />Stock: {item.currentStock || 0} pairs in inventory
                        </div>
                      </div>
                      <input
                        type="number"
                        min="0"
                        max={Math.max(0, (item.availableQuantity || item.quantity) - (item.deliveredQuantity || 0))}
                        value={item.selectedQuantity}
                        onChange={(e) => {
                          const newSaleItems = [...saleItems];
                          newSaleItems[index].selectedQuantity = parseInt(e.target.value) || 0;
                          setSaleItems(newSaleItems);
                        }}
                        style={{ width: '80px', padding: '5px', borderRadius: '4px', border: '1px solid #ddd' }}
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="form-group">
                <label>Delivery Type</label>
                <select value={deliveryType} onChange={(e) => setDeliveryType(e.target.value)}>
                  <option value="pickup">Pickup</option>
                  <option value="delivery">Delivery</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Payment Status</label>
                <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
                  <option value="not_paid">Not Paid</option>
                  <option value="half_paid">Half Paid</option>
                  <option value="full_paid">Full Paid</option>
                </select>
              </div>
              
              {paymentStatus === 'half_paid' && (
                <>
                  <div className="form-group">
                    <label>Amount Paid (LKR)</label>
                    <input
                      type="number"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      placeholder="Enter amount paid"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Cheque Number</label>
                    <input
                      type="text"
                      value={chequeNumber}
                      onChange={(e) => setChequeNumber(e.target.value)}
                      placeholder="Enter cheque number"
                    />
                  </div>
                  <div className="form-group">
                    <label>Cheque Realize Date</label>
                    <input
                      type="date"
                      value={chequeDate}
                      onChange={(e) => setChequeDate(e.target.value)}
                    />
                  </div>
                </>
              )}
              
              <div className="form-group">
                <label>Payment Notes</label>
                <textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="Additional payment notes..."
                  rows="2"
                />
              </div>
              
              <div style={{ 
                background: 'linear-gradient(45deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))', 
                padding: '20px', 
                borderRadius: '12px', 
                marginBottom: '20px' 
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  fontWeight: '700', 
                  fontSize: '1.2rem',
                  color: '#667eea'
                }}>
                  <span>Total:</span>
                  <span>LKR {saleItems.reduce((sum, item) => sum + ((item.price || item.pricePerPair || 0) * (item.selectedQuantity || 0)), 0).toLocaleString()}</span>
                </div>
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateSaleModal(false)}>Cancel</button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={handleCreateSaleFromOrderSubmit}
                  disabled={saleItems.filter(item => item.selectedQuantity > 0).length === 0}
                >
                  Complete Sale & Generate Invoice
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {showPartialDeliveryModal && selectedSale && (
        <div className="modal-overlay" onClick={() => setShowPartialDeliveryModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Add Items to Sale #{selectedSale.id.slice(-6)}</h2>
            <div className="modal-form">
              <div className="form-group">
                <label>Customer: {selectedSale.customerName}</label>
              </div>
              
              <div className="form-group">
                <label>Available Items for Additional Delivery</label>
                <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                  {partialItems.map((item, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '8px 0', 
                      borderBottom: index < partialItems.length - 1 ? '1px solid #ddd' : 'none'
                    }}>
                      <div>
                        <div style={{ fontWeight: '600', color: '#333' }}>({item.articleNumber}) {item.colour}</div>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>
                          Remaining: {item.remainingQuantity} pairs × LKR {item.price || item.pricePerPair || 0}
                        </div>
                      </div>
                      <input
                        type="number"
                        min="0"
                        max={Math.max(0, item.remainingQuantity || 0)}
                        value={item.selectedQuantity}
                        onChange={(e) => {
                          const newPartialItems = [...partialItems];
                          newPartialItems[index].selectedQuantity = parseInt(e.target.value) || 0;
                          setPartialItems(newPartialItems);
                        }}
                        style={{ width: '80px', padding: '5px', borderRadius: '4px', border: '1px solid #ddd' }}
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              <div style={{ 
                background: 'linear-gradient(45deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))', 
                padding: '20px', 
                borderRadius: '12px', 
                marginBottom: '20px' 
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  fontWeight: '700', 
                  fontSize: '1.2rem',
                  color: '#667eea'
                }}>
                  <span>Additional Total:</span>
                  <span>LKR {partialItems.reduce((sum, item) => sum + (item.price * item.selectedQuantity), 0).toLocaleString()}</span>
                </div>
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPartialDeliveryModal(false)}>Cancel</button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={handleAddPartialDelivery}
                  disabled={partialItems.filter(item => item.selectedQuantity > 0).length === 0}
                >
                  Add Items & Generate Invoice
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPaymentModal && selectedSaleForPayment && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Update Payment - Sale #{selectedSaleForPayment.id.slice(-6)}</h2>
            <div className="modal-form">
              <div className="form-group">
                <label>Customer: {selectedSaleForPayment.customerName}</label>
                <label>Current Status: {selectedSaleForPayment.paymentStatus?.replace('_', ' ').toUpperCase()}</label>
              </div>
              
              <div className="form-group">
                <label>New Payment Status</label>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button 
                    onClick={() => updatePaymentStatus(selectedSaleForPayment, 'full_paid')}
                    style={{ background: '#27ae60', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '8px', cursor: 'pointer' }}
                  >
                    Mark as Full Paid
                  </button>
                  <button 
                    onClick={() => {
                      const amount = prompt('Enter amount paid:');
                      if (amount) updatePaymentStatus(selectedSaleForPayment, 'half_paid', amount);
                    }}
                    style={{ background: '#f39c12', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '8px', cursor: 'pointer' }}
                  >
                    Mark as Half Paid
                  </button>
                </div>
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPaymentModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showInvoiceModal && invoiceData && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999 }}>
          <InvoiceGenerator 
            sale={{
              ...invoiceData,
              createdAt: invoiceData.createdAt || new Date().toISOString()
            }} 
            companyDetails={companyDetails} 
            onClose={() => {
              setShowInvoiceModal(false);
              setInvoiceData(null);
            }} 
          />
        </div>
      )}
      
      {selectedSale && !showInvoiceModal && (
        <InvoiceGenerator 
          sale={selectedSale} 
          companyDetails={companyDetails} 
          onClose={() => setSelectedSale(null)} 
        />
      )}
      
      {selectedCustomer && (
        <CustomerDetails 
          customer={selectedCustomer} 
          onClose={() => setSelectedCustomer(null)} 
        />
      )}
    </div>
  );
};

export default Sales;